import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Brain, Heart, Moon, Zap, Users, Sparkles, ArrowRight, Play } from 'lucide-react';
import { useDemo } from '@/contexts/DemoContext';
import { LandingCommunitySessions } from '@/components/landing/LandingCommunitySessions';

const logoUrl = '/lovable-uploads/voicethera-logo-new.png';


const LandingPage = () => {
  const navigate = useNavigate();
  const { setDemoMode } = useDemo();

  // Debug logging to confirm landing page is shown
  console.log('🎯 Landing Page rendered for non-authenticated user');

  const handleGetStarted = () => {
    navigate('/auth');
  };

  const handleTryDemo = () => {
    setDemoMode(true);
    navigate('/app');
  };

  const features = [
    {
      icon: Brain,
      title: "AI-Powered Personalization",
      description: "Each session is uniquely crafted based on your needs, preferences, and personal journey."
    },
    {
      icon: Heart,
      title: "Voice-Guided Wellness",
      description: "Soothing, personalized voice guidance that adapts to your emotional state and goals."
    },
    {
      icon: Moon,
      title: "Sleep & Relaxation",
      description: "Specially designed sessions to help you unwind, release stress, and improve sleep quality."
    },
    {
      icon: Users,
      title: "Workplace Wellness",
      description: "Professional meditation solutions for teams, meetings, and workplace stress management."
    },
    {
      icon: Sparkles,
      title: "Advanced Meditation",
      description: "Deep meditation practices including ZEN AF and news-based mindfulness sessions."
    },
    {
      icon: Zap,
      title: "Instant Generation",
      description: "Get your personalized meditation session in seconds, whenever you need it most."
    }
  ];

  const categories = [
    { name: "Meditation", emoji: "🧘‍♀️", color: "bg-primary-soft" },
    { name: "Sleep", emoji: "🌙", color: "bg-secondary-soft" },
    { name: "Anxiety Relief", emoji: "🌸", color: "bg-primary-soft" },
    { name: "Motivation", emoji: "⚡", color: "bg-accent/20" },
    { name: "Self Love", emoji: "💖", color: "bg-secondary-soft" },
    { name: "Memory", emoji: "🧠", color: "bg-primary-soft" }
  ];

  return (
    <div className="min-h-screen bg-gradient-wellness">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <img src={logoUrl} alt="VoiceThera" className="h-10 w-10" />
              <span className="text-xl font-light text-foreground">VoiceThera</span>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/community')}
                className="meditation-button font-light text-muted-foreground hover:text-foreground"
              >
                <Users className="h-4 w-4 mr-2" />
                Community
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/auth')}
                className="meditation-button font-light text-muted-foreground hover:text-foreground"
              >
                Sign In
              </Button>
              <Button
                onClick={handleGetStarted}
                className="meditation-button bg-primary hover:bg-primary/90 text-primary-foreground font-light"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 text-center">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="flex justify-center mb-8">
            <img src={logoUrl} alt="VoiceThera" className="h-40 w-40 animate-pulse-soft" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-light text-foreground leading-tight">
            Welcome to <span className="text-primary">VoiceThera</span>
            <br />
            AI-Powered Wellness for Your Soul
          </h1>
          
          <p className="text-xl text-muted-foreground font-light max-w-2xl mx-auto leading-relaxed">
            Transform your mind with personalized meditation and wellness sessions. Our advanced AI 
            creates unique experiences tailored to your needs, preferences, and personal journey.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
            <Button
              onClick={handleGetStarted}
              size="lg"
              className="meditation-button bg-primary hover:bg-primary/90 text-primary-foreground font-light px-8 py-3 text-lg rounded-full"
            >
              Start Your Journey
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <Button
              onClick={handleTryDemo}
              variant="outline"
              size="lg"
              className="meditation-button border-primary/30 text-primary hover:bg-primary/10 font-light px-8 py-3 text-lg rounded-full"
            >
              <Play className="mr-2 h-5 w-5" />
              Try Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Intro Video Section */}
      <section className="container mx-auto px-6 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-light text-foreground mb-4">
              See VoiceThera in Action
            </h2>
          </div>
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden shadow-lg border border-border/30">
            <iframe
              src="https://www.youtube.com/embed/shxEzD69EUo"
              title="VoiceThera Intro Video"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="container mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-light text-foreground mb-4">
            Choose Your Path to Wellness
          </h2>
          <p className="text-muted-foreground font-light max-w-2xl mx-auto">
            From meditation and sleep to anxiety relief and motivation - find the perfect session for your current needs.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 max-w-4xl mx-auto">
          {categories.map((category, index) => (
            <Card 
              key={category.name} 
              className={`meditation-card text-center p-4 ${category.color} border-border/30 hover:border-primary/30`}
            >
              <div className="text-2xl mb-2">{category.emoji}</div>
              <p className="text-sm font-light text-foreground">{category.name}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* Community Sessions Section */}
      <section className="container mx-auto px-6 py-16 bg-primary/5 rounded-2xl">
        <LandingCommunitySessions />
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h2 className="text-3xl font-light text-foreground mb-4">
            Why Choose This App?
          </h2>
          <p className="text-muted-foreground font-light max-w-2xl mx-auto">
            Experience the future of personalized wellness with our advanced AI technology.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <Card key={feature.title} className="meditation-card bg-card/80 backdrop-blur-sm border-border/50 hover:border-primary/30">
              <CardHeader className="text-center pb-4">
                <div className="inline-flex items-center justify-center h-12 w-12 bg-primary-soft rounded-full mb-4 mx-auto">
                  <feature.icon className="h-6 w-6 text-primary" />
                </div>
                <CardTitle className="text-lg font-medium text-foreground">
                  {feature.title}
                </CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <CardDescription className="text-muted-foreground font-light leading-relaxed">
                  {feature.description}
                </CardDescription>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-6 py-20">
        <Card className="meditation-card max-w-3xl mx-auto text-center wellness-soft-gradient border-primary/20">
          <CardHeader className="pb-6">
            <CardTitle className="text-3xl font-light text-foreground mb-4">
              Ready to Transform Your Wellness Journey?
            </CardTitle>
            <CardDescription className="text-lg text-muted-foreground font-light">
              Join thousands who have discovered the power of personalized AI meditation.
              Start your free journey today.
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={handleGetStarted}
                size="lg"
                className="meditation-button bg-primary hover:bg-primary/90 text-primary-foreground font-light px-10 py-4 text-lg rounded-full"
              >
                Create Free Account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              
              <Button
                onClick={handleTryDemo}
                variant="outline"
                size="lg"
                className="meditation-button border-primary/30 text-primary hover:bg-primary/10 font-light px-10 py-4 text-lg rounded-full"
              >
                <Play className="mr-2 h-5 w-5" />
                Experience Demo
              </Button>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/50 bg-card/30 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center space-x-3 mb-4 md:mb-0">
              <img src={logoUrl} alt="VoiceThera" className="h-8 w-8" />
              <span className="text-foreground font-light">VoiceThera</span>
            </div>
            <p className="text-sm text-muted-foreground font-light">
              © 2024 VoiceThera. AI-Powered Personalized Wellness Sessions.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;